/**
 * Who inherits into S1 when an Event is bound to a Track.
 *
 * Finding F7 (docs/9-exploration/MOSSO-DEMO-FINDINGS-2026-08-25.md): the
 * inheritance loop skipped the initiating user on the comment
 * "facilitator already added by createSession". They were not — `createSession`
 * only stamps `facilitatorId` and `participant_ids` on the session row; it
 * writes no `session_participants` row, and the V3 event's local
 * `participants` array starts empty. So a facilitator who was also a track
 * member vanished from their own roster: 3 of 4 track leads inherited, and the
 * fourth had to be re-added by hand.
 *
 * The rule, stated once and tested: everyone on the track roster inherits, and
 * the initiator is always present — as facilitator, unless the track already
 * names them decider, which is a stronger claim and wins.
 */

export type InheritedRole = 'facilitator' | 'decider' | 'contributor' | 'observer';

export interface TrackRosterRow {
  user_id: string;
  role?: string | null;
}

export interface InheritedParticipant {
  userId: string;
  role: InheritedRole;
  /** True for the user creating the Event — they run it, so they are never dropped. */
  isInitiator: boolean;
}

function roleFromTrack(role: string | null | undefined): InheritedRole {
  if (role === 'decider') return 'decider';
  if (role === 'observer') return 'observer';
  return 'contributor';
}

export function deriveInheritedRoster(
  trackRows: readonly TrackRosterRow[] | null | undefined,
  initiatorUserId: string | null | undefined,
): InheritedParticipant[] {
  const out: InheritedParticipant[] = [];
  const seen = new Set<string>();

  for (const row of trackRows ?? []) {
    const userId = row?.user_id;
    if (!userId || seen.has(userId)) continue;
    seen.add(userId);
    const isInitiator = Boolean(initiatorUserId) && userId === initiatorUserId;
    const trackRole = roleFromTrack(row.role);
    out.push({
      userId,
      // Running the event outranks a generic contributor seat, but a track that
      // already named this person decider keeps that.
      role: isInitiator && trackRole !== 'decider' ? 'facilitator' : trackRole,
      isInitiator,
    });
  }

  // An initiator who is not on the track roster still runs the event.
  if (initiatorUserId && !seen.has(initiatorUserId)) {
    out.unshift({ userId: initiatorUserId, role: 'facilitator', isInitiator: true });
  }

  return out;
}

/**
 * Roles for the effective participant set (cycle → track, users + groups).
 *
 * `resolveEffectiveParticipantsForTrack` answers *who* is in the set; it does
 * not carry a role. Roles live on the rows, so we look them back up here:
 * a track seat is the more specific claim and beats a cycle seat, and anyone
 * who only reaches the set through a group is a contributor.
 *
 * This exists because reading `track_participants` alone dropped a
 * cycle-level committee out of its own S1. A committee seated at the cycle
 * with no track rows opened every session with a roster of one — the person
 * who created it.
 */
export function mergeInheritedRoles(
  effectiveUserIds: readonly string[],
  cycleRows: readonly TrackRosterRow[] | null | undefined,
  trackRows: readonly TrackRosterRow[] | null | undefined,
): TrackRosterRow[] {
  const roleOf = new Map<string, string | null | undefined>();
  for (const row of cycleRows ?? []) {
    if (row?.user_id) roleOf.set(row.user_id, row.role);
  }
  // Track second: the narrower scope overwrites the cycle's role.
  for (const row of trackRows ?? []) {
    if (row?.user_id) roleOf.set(row.user_id, row.role);
  }

  const seen = new Set<string>();
  const out: TrackRosterRow[] = [];
  for (const userId of effectiveUserIds) {
    if (!userId || seen.has(userId)) continue;
    seen.add(userId);
    out.push({ user_id: userId, role: roleOf.get(userId) ?? null });
  }
  return out;
}
